import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Clock, BookOpen, Users, Award, ChevronRight, CheckCircle,
  Play, FileText, Star, Lock, ChevronDown, ChevronUp, ArrowLeft,
  HelpCircle, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LINE_COLORS = {
  kids: 'from-purple-600 to-pink-600',
  teens: 'from-blue-600 to-cyan-600',
  adultos: 'from-emerald-600 to-teal-600',
  profesionales: 'from-amber-600 to-orange-600',
  social: 'from-rose-600 to-pink-700',
  mediadores: 'from-violet-700 to-blue-700',
};
const LINE_LABELS = { kids: 'Para Niños', teens: 'Para Jóvenes', adultos: 'Para Adultos', profesionales: 'Profesionales', social: 'Impulso IA', mediadores: 'Mediadores del Futuro' };

const WACA_LOGO = "https://static.prod-images.emergentagent.com/jobs/cf4b6ddd-cd8a-43a0-80c0-50a20361cba4/images/3be115c4bea55831ae8e7a94864fbe6ccfd9d215db4a9237d5ba56321562dcf3.jpeg";
const N369_SEAL = "https://static.prod-images.emergentagent.com/jobs/cf4b6ddd-cd8a-43a0-80c0-50a20361cba4/images/1fca156725e4149dee6895773a290d321ce04b2222a7b6d468f342a9ac1534f8.jpeg";

// Compact alliance strip for course hero
const AllianceStrip = () => (
  <div className="flex items-center justify-center gap-6 py-3 px-4 bg-black/30 border-b border-white/10">
    <div className="flex items-center gap-2.5">
      <img src={WACA_LOGO} alt="WACA" className="w-9 h-9 rounded-xl object-cover border-2 border-amber-400/50 shadow-lg" />
      <div>
        <span className="text-white font-bold text-sm">WACA</span>
        <span className="text-white/50 text-[10px] ml-1.5">by Neuralia369</span>
      </div>
    </div>
    <div className="h-6 w-px bg-white/15" />
    <div className="border border-white/20 rounded-full px-3 py-1 bg-white/5">
      <span className="text-[10px] tracking-widest uppercase font-bold text-white/70">Alianza Académica</span>
    </div>
    <div className="h-6 w-px bg-white/15" />
    <div className="flex items-center gap-2.5">
      <div>
        <span className="text-white font-bold text-sm">Neuralia369</span>
        <span className="text-white/50 text-[10px] ml-1.5">by WACA</span>
      </div>
      <img src={N369_SEAL} alt="Neuralia369 Academy" className="w-9 h-9 rounded-xl object-cover border-2 border-indigo-400/50 shadow-lg" />
    </div>
  </div>
);
const LESSON_ICONS = { video: Play, lectura: FileText, test: Star, proyecto: Award };

// ── Necesito Ayuda floating button (course page version) ──────────────────
function CoursePageHelpButton() {
  const [open, setOpen] = useState(false);
  const DEFAULT_LINES = [
    { country: 'Perú', name: 'Línea 100 — MIMP', number: '100' },
    { country: 'México', name: 'SAPTEL', number: '55 5259-8121' },
    { country: 'Chile', name: 'Fono Orientación', number: '800 200 818' },
    { country: 'Global', name: 'Crisis Text Line', number: 'Text HOME to 741741' },
  ];
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="course-necesito-ayuda-btn"
        className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-3 rounded-2xl bg-red-600 hover:bg-red-500 text-white text-sm font-bold shadow-xl shadow-red-900/40 transition hover:scale-105 active:scale-95 border border-red-500/50">
        <HelpCircle size={16} /> Necesito Ayuda
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-md bg-[#080F20] border border-red-500/30 rounded-3xl shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-600/20 flex items-center justify-center">
                  <HelpCircle size={20} className="text-red-400" />
                </div>
                <p className="text-white font-bold">Líneas de apoyo disponibles</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-white p-1"><X size={18} /></button>
            </div>
            <p className="text-[#94A3B8] text-xs">Recuerda: no estás solo/a. Hay personas disponibles para escucharte ahora mismo.</p>
            <div className="space-y-2">
              {DEFAULT_LINES.map((hl, i) => (
                <div key={i} className="flex items-center justify-between bg-white/5 border border-[rgba(148,163,184,0.10)] rounded-xl px-4 py-3">
                  <div>
                    <p className="text-white text-sm font-semibold">{hl.name}</p>
                    <p className="text-[#94A3B8] text-xs">{hl.country}</p>
                  </div>
                  <a href={`tel:${hl.number}`} className="text-emerald-400 font-bold text-sm">{hl.number}</a>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-center text-[#94A3B8]/50">Programa Mediadores del Futuro · Neuralia369 Academy × WACA</p>
          </div>
        </div>
      )}
    </>
  );
}

function ModuleAccordion({ mod, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border border-[rgba(148,163,184,0.14)] rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-[rgba(8,15,32,0.5)] hover:bg-[rgba(8,15,32,0.8)] transition text-left">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 rounded-full bg-indigo-600/30 text-indigo-400 text-xs font-bold flex items-center justify-center">{index + 1}</span>
          <div>
            <p className="text-sm font-semibold text-white">{mod.title}</p>
            {mod.description && <p className="text-xs text-[#94A3B8] mt-0.5">{mod.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 text-[#94A3B8]">
          <span className="text-xs">{mod.lessons?.length || 0} lecciones</span>
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </div>
      </button>
      {open && mod.lessons?.length > 0 && (
        <div className="divide-y divide-[rgba(148,163,184,0.08)]">
          {mod.lessons.map((l, li) => {
            const Icon = LESSON_ICONS[l.type] || FileText;
            return (
              <div key={l.id} className="flex items-center gap-3 px-5 py-3 bg-[rgba(8,15,32,0.3)]">
                <div className="w-6 h-6 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Icon size={11} className="text-[#94A3B8]" />
                </div>
                <span className="text-xs text-[#94A3B8] flex-1">{l.title}</span>
                {l.duration_min && <span className="text-[10px] text-[#94A3B8]/60">{l.duration_min} min</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AcademyCoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, authFetch } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState('');
  // Coupon / payment state
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null); // null | {valid, discount_pct, final_price, is_free, description, error}
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await fetch(`${API}/academy/courses/${courseId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Curso no encontrado');
        setCourse(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [courseId]);

  useEffect(() => {
    if (!user) return;
    authFetch(`${API}/academy/my-courses`).then(r => r.json()).then(d => {
      const found = d.enrollments?.find(e => e.course_id === courseId?.toUpperCase());
      if (found) setEnrollment(found);
    }).catch(() => {});
  }, [user, courseId, authFetch]);

  const handleEnroll = async () => {
    if (!user) { navigate('/acceso-academy'); return; }
    setEnrolling(true);
    try {
      const res = await authFetch(`${API}/academy/enroll/${courseId}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setEnrollment({ course_id: courseId?.toUpperCase(), progress_pct: 0, enrollment_id: data.enrollment_id });
        navigate(`/academy/aula/${courseId}`);
      }
    } catch (e) {
      setError('Error al inscribirse. Inténtalo de nuevo.');
    } finally {
      setEnrolling(false);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCheckingCoupon(true); setCouponResult(null);
    try {
      const res = await authFetch(`${API}/academy/payment/validate-coupon`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode.trim().toUpperCase(), course_id: courseId })
      });
      const data = await res.json();
      setCouponResult(data);
    } catch (e) { setCouponResult({ valid: false, error: 'Error al validar' }); }
    setCheckingCoupon(false);
  };

  const handlePaidEnroll = async () => {
    if (!user) { navigate('/acceso-academy'); return; }
    // If 100% free coupon
    if (couponResult?.is_free) {
      setEnrolling(true);
      try {
        const res = await authFetch(`${API}/academy/payment/enroll-free`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ course_id: courseId, coupon_code: couponCode.trim().toUpperCase() })
        });
        const data = await res.json();
        if (data.success) {
          setEnrollment({ course_id: courseId?.toUpperCase(), progress_pct: 0 });
          navigate(`/academy/aula/${courseId}`);
        }
      } catch (e) { setError('Error al inscribirse con beca.'); }
      finally { setEnrolling(false); }
      return;
    }
    // Stripe checkout
    setCheckoutLoading(true);
    try {
      const res = await authFetch(`${API}/academy/payment/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: courseId })
      });
      const data = await res.json();
      if (data.checkout_url) { window.location.href = data.checkout_url; }
      else { setError(data.detail || 'Error al crear sesión de pago'); }
    } catch (e) { setError('No se pudo conectar con la pasarela de pago.'); }
    finally { setCheckoutLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !course) return (
    <div className="min-h-screen bg-[#030712] flex flex-col items-center justify-center gap-4">
      <p className="text-red-400">{error || 'Curso no encontrado'}</p>
      <Link to="/academy" className="text-indigo-400 hover:underline">← Volver al catálogo</Link>
    </div>
  );

  const isKids = ['kids', 'teens'].includes(course.line);
  const isSocial = course.line === 'social' || course.code?.startsWith('N');
  const hours = (course.weeks || 6) * 3;

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="academy-course-page">
      {/* Floating help button for social courses */}
      {isSocial && <CoursePageHelpButton />}
      {/* Hero */}
      <div className={`bg-gradient-to-br ${LINE_COLORS[course.line] || 'from-indigo-700 to-indigo-900'} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-black/40" />
        {/* Alliance strip */}
        <div className="relative"><AllianceStrip /></div>
        <div className="relative max-w-5xl mx-auto px-4 py-12">
          <Link to="/academy" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition">
            <ArrowLeft size={15} /> Catálogo
          </Link>
          <div className="flex flex-wrap items-start gap-6">
            <div className="flex-1 min-w-0 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs bg-white/20 text-white rounded-full px-3 py-1">{LINE_LABELS[course.line]}</span>
                {course.nuri_mascot && <span className="text-xs bg-white/20 text-white rounded-full px-3 py-1">Mascota Nuri</span>}
                {course.is_scholarship_eligible && <span className="text-xs bg-amber-400/30 text-amber-200 rounded-full px-3 py-1">Becas disponibles</span>}
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight">{course.name}</h1>
              <p className="text-white/80 text-base max-w-2xl">{course.description_long || course.description_short}</p>
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <span className="flex items-center gap-1.5"><Clock size={14} /> {course.weeks} semanas</span>
                <span className="flex items-center gap-1.5"><BookOpen size={14} /> {course.modules_count} módulos</span>
                <span className="flex items-center gap-1.5"><Users size={14} /> {course.age_min}-{course.age_max} años</span>
                <span className="flex items-center gap-1.5"><Award size={14} /> {hours}h certificadas</span>
              </div>
            </div>

            {/* Enroll card */}
            <div className="w-full sm:w-72 bg-[rgba(8,15,32,0.90)] rounded-2xl p-6 border border-white/10 space-y-4 flex-shrink-0" data-testid="enroll-card">
              <div className="text-center">
                {couponResult?.valid ? (
                  <div>
                    <p className="text-lg font-black text-[#94A3B8] line-through">${course.price_usd} USD</p>
                    <p className="text-3xl font-black text-emerald-400">${couponResult.final_price} <span className="text-sm font-normal text-[#94A3B8]">USD</span></p>
                    <p className="text-xs text-emerald-400 mt-1">-{couponResult.discount_pct}% · {couponResult.description}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-3xl font-black text-white">{course.price_usd === 0 ? 'Gratis' : `$${course.price_usd}`} <span className="text-sm font-normal text-[#94A3B8]">{course.price_usd > 0 ? 'USD' : ''}</span></p>
                    {course.is_scholarship_eligible && <p className="text-xs text-amber-400 mt-1">Cupo becado disponible</p>}
                  </div>
                )}
              </div>

              {enrollment ? (
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs text-[#94A3B8] mb-1">
                      <span>Progreso</span><span>{enrollment.progress_pct || 0}%</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full">
                      <div className="h-2 bg-emerald-500 rounded-full transition-all" style={{ width: `${enrollment.progress_pct || 0}%` }} />
                    </div>
                  </div>
                  <Link to={`/academy/aula/${courseId}`} data-testid="continue-course-btn"
                    className="block w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-center transition">
                    Continuar curso →
                  </Link>
                </div>
              ) : course.price_usd > 0 ? (
                <div className="space-y-3">
                  {/* Coupon field */}
                  <div className="space-y-2">
                    <p className="text-xs text-[#94A3B8]">¿Tienes cupón o beca WACA?</p>
                    <div className="flex gap-2">
                      <input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        onKeyDown={e => e.key === 'Enter' && handleValidateCoupon()}
                        placeholder="WACAFREE, WACA50..."
                        data-testid="coupon-input"
                        className="flex-1 bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] focus:border-amber-500/40 rounded-lg px-3 py-2 text-white text-xs placeholder-[#94A3B8]/40 focus:outline-none transition font-mono" />
                      <button onClick={handleValidateCoupon} disabled={!couponCode.trim() || checkingCoupon} data-testid="validate-coupon-btn"
                        className="px-2.5 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-bold border border-amber-500/20 transition disabled:opacity-40">
                        {checkingCoupon ? '...' : 'OK'}
                      </button>
                    </div>
                    {couponResult && (
                      <p className={`text-[11px] px-2.5 py-1.5 rounded-lg ${couponResult.valid ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                        {couponResult.valid ? `✓ ${couponResult.description}` : couponResult.error}
                      </p>
                    )}
                  </div>
                  {couponResult?.is_free ? (
                    <button onClick={handlePaidEnroll} disabled={enrolling} data-testid="enroll-free-btn"
                      className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition disabled:opacity-50">
                      {enrolling ? 'Inscribiendo...' : 'Inscribirme con beca gratis'}
                    </button>
                  ) : (
                    <button onClick={handlePaidEnroll} disabled={checkoutLoading} data-testid="checkout-btn"
                      className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold transition disabled:opacity-50 flex items-center justify-center gap-2">
                      {checkoutLoading ? 'Redirigiendo...' : (
                        <>
                          {user ? (couponResult?.valid ? `Pagar $${couponResult.final_price}` : `Comprar acceso — $${course.price_usd}`) : 'Acceder para pagar'}
                          {!checkoutLoading && <ChevronRight size={16} />}
                        </>
                      )}
                    </button>
                  )}
                  {/* WACA becas note */}
                  <p className="text-[10px] text-center text-[#94A3B8]/60">
                    Instituciones: usa código <span className="text-amber-400 font-mono">WACAFREE</span>, <span className="text-amber-400 font-mono">PERU2026</span>
                  </p>
                </div>
              ) : (
                <button onClick={handleEnroll} disabled={enrolling} data-testid="enroll-btn"
                  className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold transition disabled:opacity-50">
                  {enrolling ? 'Inscribiendo...' : user ? 'Inscribirme gratis' : 'Acceder e inscribirse'}
                </button>
              )}}

              <ul className="space-y-2">
                {course.objectives?.slice(0, 3).map((obj, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[#94A3B8]">
                    <CheckCircle size={13} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    {obj}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-4 py-10 grid lg:grid-cols-3 gap-8">
        {/* Main: modules */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-xl font-bold text-white">Contenido del curso</h2>
          {course.modules?.length > 0 ? (
            course.modules.map((m, i) => <ModuleAccordion key={m.id} mod={m} index={i} />)
          ) : (
            <div className="rounded-2xl border border-[rgba(148,163,184,0.14)] p-8 text-center text-[#94A3B8]">
              <BookOpen size={32} className="mx-auto mb-3 opacity-40" />
              <p>El contenido de este curso estará disponible próximamente.</p>
            </div>
          )}
        </div>

        {/* Sidebar: details */}
        <div className="space-y-5">
          <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-5 space-y-4">
            <h3 className="font-bold text-white text-sm">Lo que aprenderás</h3>
            <ul className="space-y-2">
              {course.objectives?.map((obj, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-[#94A3B8]">
                  <CheckCircle size={13} className="text-indigo-400 flex-shrink-0 mt-0.5" />
                  {obj}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-5 space-y-3">
            <h3 className="font-bold text-white text-sm">Detalles del curso</h3>
            <div className="space-y-2 text-xs text-[#94A3B8]">
              <div className="flex justify-between"><span>Nivel</span><span className="text-white">{LINE_LABELS[course.line]}</span></div>
              <div className="flex justify-between"><span>Edad</span><span className="text-white">{course.age_min}-{course.age_max} años</span></div>
              <div className="flex justify-between"><span>Duración</span><span className="text-white">{course.weeks} semanas</span></div>
              <div className="flex justify-between"><span>Módulos</span><span className="text-white">{course.modules_count}</span></div>
              <div className="flex justify-between"><span>Horas certificadas</span><span className="text-white">{hours}h</span></div>
              <div className="flex justify-between"><span>Idioma</span><span className="text-white">Español</span></div>
              {course.has_tests && <div className="flex justify-between"><span>Tests</span><span className="text-emerald-400">Sí, auto-corregibles</span></div>}
              {course.has_final_project && <div className="flex justify-between"><span>Proyecto final</span><span className="text-emerald-400">Incluido</span></div>}
              {course.has_ai_chat && <div className="flex justify-between"><span>Chat con Aynara</span><span className="text-indigo-400">+13 años</span></div>}
            </div>
          </div>

          {isKids && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 space-y-2">
              <p className="text-amber-400 font-bold text-sm">Para familias</p>
              <p className="text-xs text-[#94A3B8]">Este curso lo gestiona un adulto responsable. Los menores aprenden con actividades guiadas sin chat directo con IA.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
